// The server re-encodes every image to a small JPEG for the PDFs, so these are only abuse guards
// (same values as the backend). Compression here is a best-effort optimization to save mobile data.
export const MAX_UPLOAD_IMAGE_BYTES = 25 * 1024 * 1024;
export const MAX_UPLOAD_BATCH_BYTES = 200 * 1024 * 1024;
export const MAX_EVIDENCE_FILES = 20;
export const COMPRESSION_TARGET_BYTES = 2 * 1024 * 1024;
// Above these the browser could run out of memory decoding: send the original, the server resizes it.
const MAX_CLIENT_DECODE_PIXELS = 40000000;
const MAX_CLIENT_DECODE_SIDE = 16384;
const MAX_OUTPUT_SIDE = 2560;

const readExifOrientation = (view, start, end) => {
  if (start + 8 > end) return 1;
  const order = view.getUint16(start);
  if (![0x4949, 0x4d4d].includes(order)) return 1;
  const littleEndian = order === 0x4949;
  if (view.getUint16(start + 2, littleEndian) !== 42) return 1;
  const directory = start + view.getUint32(start + 4, littleEndian);
  if (directory < start + 8 || directory + 2 > end) return 1;
  const count = view.getUint16(directory, littleEndian);
  for (let entry = directory + 2; entry + 12 <= end && entry < directory + 2 + count * 12; entry += 12) {
    if (view.getUint16(entry, littleEndian) === 0x0112
      && view.getUint16(entry + 2, littleEndian) === 3 && view.getUint32(entry + 4, littleEndian) === 1) {
      const orientation = view.getUint16(entry + 8, littleEndian);
      return orientation >= 1 && orientation <= 8 ? orientation : 1;
    }
  }
  return 1;
};

// Read dimensions before decoding, to avoid allocating an oversized canvas on phones.
const readImageMetadata = (buffer) => {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  if (bytes.length >= 24 && [137, 80, 78, 71, 13, 10, 26, 10].every((value, index) => bytes[index] === value)
    && view.getUint32(12) === 0x49484452) {
    let animated = false;
    for (let offset = 8; offset + 12 <= bytes.length;) {
      const length = view.getUint32(offset);
      if (length > bytes.length - offset - 12) throw new Error("El archivo PNG está incompleto.");
      const chunkType = view.getUint32(offset + 4);
      if (chunkType === 0x6163544c) animated = true; // APNG acTL, before image data.
      if (chunkType === 0x49444154) break;
      offset += length + 12;
    }
    return { type: "image/png", width: view.getUint32(16), height: view.getUint32(20), animated };
  }
  const signature = String.fromCharCode(...bytes.slice(0, 6));
  if (bytes.length >= 10 && ["GIF87a", "GIF89a"].includes(signature)) {
    return { type: "image/gif", width: view.getUint16(6, true), height: view.getUint16(8, true) };
  }
  if (bytes[0] === 0xff && bytes[1] === 0xd8) {
    let offset = 2;
    let orientation = 1;
    while (offset + 3 < bytes.length) {
      if (bytes[offset++] !== 0xff) break;
      while (bytes[offset] === 0xff) offset++;
      const marker = bytes[offset++];
      if ([0xd9, 0xda].includes(marker)) break;
      if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue;
      if (offset + 2 > bytes.length) break;
      const length = view.getUint16(offset);
      if (length < 2 || offset + length > bytes.length) break;
      if (marker === 0xe1 && length >= 16 && view.getUint32(offset + 2) === 0x45786966
        && view.getUint16(offset + 6) === 0) {
        orientation = readExifOrientation(view, offset + 8, offset + length);
      }
      if ([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf].includes(marker) && length >= 8) {
        return { type: "image/jpeg", width: view.getUint16(offset + 5), height: view.getUint16(offset + 3), orientation };
      }
      offset += length;
    }
  }
  throw new Error("El archivo no contiene una imagen JPG, PNG o GIF válida.");
};

export const validateEvidenceBatch = (files) => {
  if (files.length > MAX_EVIDENCE_FILES) throw new Error("Solo se permiten 20 imágenes por envío.");
  if (files.some((file) => !file.size || file.size > MAX_UPLOAD_IMAGE_BYTES)) {
    throw new Error("Cada imagen debe tener contenido y pesar como máximo 25 MB.");
  }
  if (files.reduce((total, file) => total + file.size, 0) > MAX_UPLOAD_BATCH_BYTES) {
    throw new Error("Las imágenes seleccionadas superan 200 MB. Selecciona menos imágenes para guardar.");
  }
};

// Best effort: never mutates the original file and falls back to it whenever the browser cannot
// shrink it (the server always normalizes). Only malformed or unsupported files are rejected.
export const compressEvidenceImage = async (file) => {
  if (!["image/jpeg", "image/jpg", "image/png", "image/gif"].includes(file.type)) {
    throw new Error("Formato no admitido. Selecciona una imagen JPG, PNG o GIF; convierte HEIC/WebP antes de adjuntar.");
  }
  if (!file.size || file.size > MAX_UPLOAD_IMAGE_BYTES) {
    throw new Error("La imagen está vacía o supera 25 MB. Exporta una copia más pequeña.");
  }
  const metadata = readImageMetadata(await file.arrayBuffer());
  if (metadata.type !== (file.type === "image/jpg" ? "image/jpeg" : file.type)) {
    throw new Error("El contenido de la imagen no coincide con su formato declarado.");
  }
  // GIF/animated PNG cannot be re-encoded by canvas without losing frames, and the PDF only needs one.
  if (metadata.type === "image/gif" || metadata.animated) return file;
  if (!metadata.width || !metadata.height || metadata.width * metadata.height > MAX_CLIENT_DECODE_PIXELS
    || Math.max(metadata.width, metadata.height) > MAX_CLIENT_DECODE_SIDE) return file;
  // Small, correctly oriented originals stay byte-for-byte.
  if (file.size <= COMPRESSION_TARGET_BYTES && !(metadata.orientation > 1)) return file;
  const sourceUrl = URL.createObjectURL(file);
  const image = new Image();
  let canvas;
  try {
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = () => reject(new Error("decode"));
      image.src = sourceUrl;
    });
    // Modern browser decoding applies EXIF orientation; verify on actual Android/iPhone devices.
    const scale = Math.min(1, MAX_OUTPUT_SIDE / Math.max(image.naturalWidth, image.naturalHeight));
    canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext("2d");
    if (!context) return file;
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const type = metadata.type;
    const encode = (quality) => new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob && blob.type === type ? blob : null), type, quality);
    });
    let blob = await encode(0.85);
    if (type === "image/jpeg" && blob && blob.size > COMPRESSION_TARGET_BYTES) blob = await encode(0.8);
    // Keep the original if the canvas could not produce something smaller (or the file was oriented).
    if (!blob || !blob.size || (blob.size >= file.size && !(metadata.orientation > 1))) return file;
    const name = type === "image/jpeg" ? file.name.replace(/\.[^.]+$/, "") + ".jpg" : file.name;
    return new File([blob], name, { type, lastModified: file.lastModified });
  } catch (error) {
    return file;
  } finally {
    URL.revokeObjectURL(sourceUrl);
    image.src = "";
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
};
